'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { searchSkills, normalizeSearchQuery, type StandardSkill } from '@/utils/skill-search'
import { Input } from '@/components/ui/input'

interface SkillSelectorProps {
  value: string[]
  onChange: (value: string[]) => void
  category?: string | null
  placeholder?: string
  disabled?: boolean
  showSelected?: boolean
}

export const SkillSelector: React.FC<SkillSelectorProps> = ({
  value = [],
  onChange,
  category,
  placeholder = '스킬을 검색하거나 입력하세요 (예: React, Python)',
  disabled = false,
  showSelected = true,
}) => {
  const { supabase } = useSupabase()
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [searchResults, setSearchResults] = useState<StandardSkill[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const selectingRef = useRef(false)

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)

    const query = inputValue.trim()
    if (query.length < 2) {
      setSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchSkills(supabase, query, category, 15)
        setSearchResults(results)
      } catch (error) {
        console.error('[SkillSelector] 검색 오류:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 250)

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [inputValue, supabase, category])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const showCustomOption = useMemo(() => {
    const query = inputValue.trim()
    if (query.length < 2) return false
    if (value.includes(query)) return false

    const normalizedQuery = normalizeSearchQuery(query)
    const hasExactMatch = searchResults.some(
      (skill) => normalizeSearchQuery(skill.name) === normalizedQuery
    )

    return !hasExactMatch
  }, [inputValue, searchResults, value])

  const handleSelectSkill = (skillName: string) => {
    selectingRef.current = true

    if (!value.includes(skillName)) onChange([...value, skillName])

    setInputValue('')
    setIsFocused(false)

    setTimeout(() => {
      selectingRef.current = false
    }, 0)
  }

  const handleRemoveSkill = (skillName: string) => {
    onChange(value.filter((s) => s !== skillName))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    if (!inputValue.trim()) return

    e.preventDefault()

    if (searchResults.length > 0 && !isSearching) {
      const firstUnselected = searchResults.find((s) => !value.includes(s.name))
      if (firstUnselected) {
        handleSelectSkill(firstUnselected.name)
        return
      }
    }

    if (showCustomOption) {
      const trimmed = inputValue.trim()
      if (!value.includes(trimmed)) handleSelectSkill(trimmed)
    }
  }

  const handleInputBlur = () => {
    setTimeout(() => {
      if (selectingRef.current) return
      setIsFocused(false)
    }, 120)
  }

  const renderDropdown = () => {
    const query = inputValue.trim()
    const shouldOpen =
      isFocused && (isSearching || searchResults.length > 0 || showCustomOption || query.length >= 2)

    if (!shouldOpen) return null

    return (
      <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="max-h-60 overflow-y-auto p-1">
          {isSearching && <div className="px-3 py-2 text-sm text-gray-500">검색 중...</div>}

          {!isSearching && searchResults.length > 0 && (
            <div className="space-y-1">
              {searchResults.map((skill) => {
                const isSelected = value.includes(skill.name)
                const disabledItem = isSelected || disabled

                return (
                  <button
                    type="button"
                    key={skill.id}
                    className={[
                      'w-full rounded-lg px-3 py-2 text-left text-sm border border-transparent',
                      disabledItem ? 'bg-blue-50 text-gray-400' : 'hover:bg-gray-50 text-gray-900',
                    ].join(' ')}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => !disabledItem && handleSelectSkill(skill.name)}
                    disabled={disabledItem}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-col">
                        <span>{skill.name}</span>
                        {skill.category && <span className="text-xs text-gray-500">{skill.category}</span>}
                      </div>
                      {isSelected && <span className="text-xs text-blue-600">선택됨</span>}
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {!isSearching && showCustomOption && !disabled && (
            <div className="mt-1 border-t border-gray-100 pt-1">
              <button
                type="button"
                className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectSkill(query)}
              >
                <span className="font-medium text-blue-600">‘{query}’ 직접 추가</span>
                <span className="ml-2 text-xs text-gray-500">목록에 없으면 직접 입력할 수 있어요</span>
              </button>
            </div>
          )}

          {!isSearching && searchResults.length === 0 && !showCustomOption && query.length >= 2 && (
            <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="relative" ref={containerRef}>
        <Input
          type="text"
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full"
        />
        {renderDropdown()}
      </div>

      {showSelected && value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((skill) => (
            <div
              key={skill}
              className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1"
            >
              <span className="text-sm text-gray-900">{skill}</span>

              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveSkill(skill)}
                  className="text-xs font-medium text-gray-500 hover:text-blue-600"
                  aria-label={`${skill} 삭제`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
